import React from 'react';
import { Lock } from 'lucide-react';

const C = {
  orange: "#F47920",
  orangeHover: "#E06810",
  orangeLight: "rgba(244,121,32,0.07)",
  orangeBorder: "rgba(244,121,32,0.2)",
  dark: "#1E2A3A",
  darkSoft: "#2D3B4E",
  green: "#22C55E",
  greenLight: "#DCFCE7",
  greenBorder: "#BBF7D0",
  purple: "#8B5CF6",
  purpleLight: "#F3F0FF",
  purpleBorder: "#DDD6FE",
  blue: "#3B82F6",
  blueLight: "#EFF6FF",
  amber: "#F59E0B",
  rose: "#F43F5E",
  teal: "#14B8A6",
  indigo: "#6366F1",
  cyan: "#06B6D4",
  g50: "#F9FAFB", g100: "#F3F4F6", g200: "#E5E7EB",
  g300: "#D1D5DB", g400: "#9CA3AF", g500: "#6B7280",
  g600: "#4B5563", g700: "#374151", g900: "#111827",
  white: "#FFFFFF",
};

const SCALE_COLORS = [
  C.orange, C.blue, C.teal, C.purple, C.green, C.amber, C.rose, C.indigo
];

const ALL_SCALES = {
  "1": { name: "Analytical Thinking", shortName: "Analytical", icon: "🧩" },
  "2": { name: "Communication & Clarity", shortName: "Communication", icon: "💬" },
  "3": { name: "Adaptability & Learning", shortName: "Adaptability", icon: "🔄" },
  "4": { name: "Ownership & Results", shortName: "Ownership", icon: "🎯" },
  "5": { name: "Teamwork & Influence", shortName: "Teamwork", icon: "🤝" },
  "6": { name: "Motivation & Career", shortName: "Motivation", icon: "🚀" },
  "7": { name: "Self-Awareness & Maturity", shortName: "Self-Awareness", icon: "🪞" },
  "8": { name: "Domain Expertise", shortName: "Domain Skills", icon: "⚡" },
};

const SCALE_LEVEL_COPY = {
  "1": {
    1: "Your problem solving is weak right now. You need much stronger structure when a task is ambiguous, especially in how you break it into steps and isolate the real problem.",
    2: "Your analytical thinking is developing, but the approach still looks reactive. You notice parts of the problem, yet you need clearer structure, trade-off thinking, and stronger root-cause logic.",
    3: "You show a solid working level here. You can solve typical problems logically, but you still need more depth on alternatives, prioritization, and cause analysis.",
    4: "You demonstrate strong analytical thinking. You structure problems well, compare options clearly, and usually arrive at decisions that feel well reasoned.",
    5: "You operate at a top-tier analytical level. You handle ambiguity like a high-end specialist, find non-obvious paths, and explain your decisions with exceptional clarity.",
  },
  "2": {
    1: "Your communication is too unclear for strong interview performance. Your ideas are harder to follow than they should be, examples feel incomplete, and the message needs major improvement.",
    2: "You already show some useful communication signals, but the delivery is still uneven. You need stronger structure, clearer wording, and tighter answers.",
    3: "You communicate at a decent working level. Your message is understandable, but you still need more precision, sharper structure, and clearer emphasis on the main point.",
    4: "You demonstrate strong communication. You explain ideas clearly, stay structured, and give answers that are easy for an interviewer to evaluate.",
    5: "You communicate at an elite interview level. You sound concise, persuasive, highly structured, and close to what top specialists usually demonstrate.",
  },
  "3": {
    1: "Your adaptability is weak right now. You show limited evidence that you can learn fast or adjust effectively when conditions change.",
    2: "Your adaptability is emerging, but still inconsistent. You can react to change, though your learning speed and flexibility need clear improvement.",
    3: "You have a workable level of adaptability. You can learn and adjust in normal situations, but you do not yet show standout learning agility.",
    4: "You show a good level of adaptability. You respond well to change, learn quickly, and transfer lessons from one situation to another.",
    5: "You demonstrate top-level learning agility. You absorb new context fast, adapt with confidence, and grow at the level expected from outstanding professionals.",
  },
  "4": {
    1: "Your ownership is too weak right now. You are not yet showing enough responsibility, initiative, or clear result orientation.",
    2: "You show ownership in parts, but it is still limited. Your answers suggest contribution, yet they do not fully prove accountability or business impact.",
    3: "You show an acceptable level of ownership. You take responsibility for tasks, but your answers still need more evidence of proactive action and measurable results.",
    4: "You demonstrate strong ownership. You take responsibility, drive outcomes, and usually make it clear what changed because of your work.",
    5: "You show outstanding ownership. You lead from the front, create visible results, and demonstrate the accountability expected from top performers.",
  },
  "5": {
    1: "Your teamwork and influence are weak at the moment. You are not yet showing enough collaboration, alignment, or positive impact on other people.",
    2: "You show collaboration, but your influence is still limited. You can work with others, though you need stronger stakeholder management and broader team impact.",
    3: "You show a stable teamwork level. You collaborate reasonably well, but influence and cross-functional effectiveness are not yet a major strength.",
    4: "You demonstrate strong teamwork. You work effectively with others, build alignment, and show useful influence across stakeholders.",
    5: "You operate at a top-tier level in collaboration and influence. You bring people together, raise team effectiveness, and come across like a trusted high-level partner.",
  },
  "6": {
    1: "Your motivation is too weak for a strong interview impression. You are not yet showing clear direction, purpose, or convincing career intent.",
    2: "Your motivation is visible in parts, but it is still vague. You show interest, though your career logic and role fit need to be much clearer.",
    3: "You show a reasonable level of motivation. You appear genuinely interested, but your answers still need more depth on why this role and why now.",
    4: "You demonstrate a good level of motivation. You show clear interest, sensible career direction, and a convincing connection to the role.",
    5: "You show excellent motivation. You present a focused, credible career story and the level of intent usually expected from top specialists.",
  },
  "7": {
    1: "Your self-awareness is currently very weak. You are not yet showing enough reflection, maturity, or understanding of your own gaps and lessons learned.",
    2: "Your self-awareness is developing, but still shallow. You recognize some weaknesses, though your reflection and maturity need to become much stronger.",
    3: "You show a fair level of self-awareness. You can reflect on your strengths and gaps, but the depth of insight is still moderate.",
    4: "You demonstrate strong self-awareness. You reflect honestly, understand your growth areas, and show mature judgment about your own performance.",
    5: "You show exceptional self-awareness. You demonstrate calm maturity, clear reflection, and the kind of insight seen in top professionals.",
  },
  "8": {
    1: "Your domain expertise is far below the target level right now. You need major improvement in core tools, concepts, and role-specific depth.",
    2: "Your domain expertise is still developing. Some foundations are visible, but you need much stronger practical depth for this role.",
    3: "You show a usable level of domain expertise. You understand the essentials, but you still need deeper knowledge and sharper practical mastery.",
    4: "You demonstrate a good level of domain expertise. You show solid command of relevant skills, tools, and professional judgment for the role.",
    5: "You demonstrate elite domain expertise. You show the depth, fluency, and technical confidence expected from top specialists in the field.",
  },
};

function resolveScoreLevel(score) {
  const numeric = Number(score) || 0;
  if (numeric >= 4.5) return 5;
  if (numeric >= 3.5) return 4;
  if (numeric >= 2.5) return 3;
  if (numeric >= 1.5) return 2;
  return 1;
}

function getScaleDescription(scaleId, score) {
  const level = resolveScoreLevel(score);
  const description = SCALE_LEVEL_COPY[String(scaleId)]?.[level] || 'This competency needs more evidence before we can interpret it confidently.';
  const labels = {
    1: 'Critical gap',
    2: 'Needs work',
    3: 'Solid baseline',
    4: 'Strong level',
    5: 'Top specialist level',
  };
  return {
    level,
    label: labels[level],
    description,
  };
}

function RadarChart({ scores, size = 260, locked = false }) {
  const padding = 28;
  const canvasSize = size + padding * 2;
  const cx = canvasSize / 2, cy = canvasSize / 2;
  const levels = 5;
  const r = (size - 70) / 2;
  const n = scores.length;

  const angleSlice = (2 * Math.PI) / n;

  const getPoint = (i, val) => {
    const angle = angleSlice * i - Math.PI / 2;
    const dist = (val / 5) * r;
    return { x: cx + dist * Math.cos(angle), y: cy + dist * Math.sin(angle) };
  };

  const gridLevels = Array.from({ length: levels }, (_, l) => {
    const fraction = (l + 1) / levels;
    const pts = Array.from({ length: n }, (_, i) => {
      const angle = angleSlice * i - Math.PI / 2;
      return `${cx + r * fraction * Math.cos(angle)},${cy + r * fraction * Math.sin(angle)}`;
    });
    return pts.join(" ");
  });

  const dataPath = scores.map((s, i) => {
    const p = getPoint(i, s.score);
    return `${p.x},${p.y}`;
  }).join(" ");

  return (
    <div className="relative inline-block mx-auto">
      <svg width={canvasSize} height={canvasSize} viewBox={`0 0 ${canvasSize} ${canvasSize}`} className="overflow-visible">
        {gridLevels.map((pts, i) => (
          <polygon
            key={i}
            points={pts}
            fill="none"
            stroke={C.g200}
            strokeWidth={i === levels - 1 ? 1.5 : 0.8}
            strokeDasharray={i < levels - 1 ? "3,3" : "none"}
          />
        ))}
        {scores.map((_, i) => {
          const p = getPoint(i, 5);
          return <line key={i} x1={cx} y1={cy} x2={p.x} y2={p.y} stroke={C.g200} strokeWidth={0.8} />;
        })}
        <polygon
          points={dataPath}
          fill={`${C.orange}20`}
          stroke={C.orange}
          strokeWidth={2}
          strokeLinejoin="round"
          style={{ filter: locked ? "blur(6px)" : "none" }}
          className="transition-all duration-700"
        />
        {!locked && scores.map((s, i) => {
          const p = getPoint(i, s.score);
          return (
            <circle
              key={i}
              cx={p.x}
              cy={p.y}
              r={4}
              fill={C.white}
              stroke={s.color}
              strokeWidth={2.5}
            />
          );
        })}
        {scores.map((s, i) => {
          const angle = angleSlice * i - Math.PI / 2;
          const labelR = r + 18;
          const x = cx + labelR * Math.cos(angle);
          const y = cy + labelR * Math.sin(angle);
          const isLeft = x < cx - 10;
          const isRight = x > cx + 10;
          return (
            <text
              key={i}
              x={x}
              y={y}
              textAnchor={isLeft ? "end" : isRight ? "start" : "middle"}
              dominantBaseline="central"
              fontSize={10}
              fontWeight={600}
              fill={locked ? C.g400 : C.g600}
              style={{ filter: locked ? "blur(2.5px)" : "none" }}
            >
              {s.shortName}
            </text>
          );
        })}
      </svg>
      {locked && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/50 backdrop-blur-[2px] rounded-xl">
          <div className="w-11 h-11 rounded-xl bg-gray-100 flex items-center justify-center text-xl mb-2">🔒</div>
          <span className="text-[13px] font-semibold text-gray-600">Pro Feature</span>
        </div>
      )}
    </div>
  );
}

function ScoreBar({ scaleId, label, score, maxScore = 5, color, icon, locked = false, compact = false }) {
  const pct = (score / maxScore) * 100;
  const barH = compact ? 4 : 5;
  const descriptor = getScaleDescription(scaleId, score);

  return (
    <div className={`flex items-start gap-2.5 ${compact ? 'py-0.5' : 'py-1.5'} relative`}>
      {!compact && (
        <span className={`text-[14px] w-5 text-center transition-all ${locked ? 'blur-[3px]' : ''}`}>
          {icon}
        </span>
      )}
      <div className="flex-1 min-w-0">
        <div className="flex justify-between items-baseline mb-1">
          <span className={`text-[12px] font-semibold truncate transition-all ${locked ? 'blur-[3px] text-gray-400' : 'text-gray-700'}`}>
            {label}
          </span>
          <span className={`text-[11px] font-bold ml-2 flex-shrink-0 transition-all ${locked ? 'blur-[3px] text-gray-400' : ''}`} style={{ color: locked ? undefined : color }}>
            {score.toFixed(1)}/{maxScore}
          </span>
        </div>
        <div
          className="rounded-full bg-gray-100 overflow-hidden"
          style={{ height: `${barH}px` }}
        >
          <div 
            className={`h-full rounded-full transition-all duration-1000 ease-out ${locked ? 'blur-[3.5px] bg-gray-300' : ''}`}
            style={{ 
              width: `${pct}%`, 
              background: locked ? undefined : `linear-gradient(90deg, ${color}CC, ${color})` 
            }} 
          />
        </div>
        <div className={`mt-1.5 transition-all ${locked ? 'blur-[3px]' : ''}`}>
          <div className="text-[10px] font-semibold uppercase tracking-[0.14em] text-gray-400">
            {descriptor.label}
          </div>
          <div className="mt-0.5 text-[11px] leading-relaxed text-gray-500">
            {descriptor.description}
          </div>
        </div>
      </div>
    </div>
  );
}

function UpgradeBanner() {
  return (
    <div className="flex items-center gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200 mt-4 shadow-sm">
      <span className="text-2xl">✨</span>
      <div className="flex-1">
        <div className="text-[13px] font-bold text-slate-800 leading-tight">Unlock detailed analytics</div>
        <div className="text-[12px] text-slate-500 mt-0.5">See scores across all competency scales</div>
      </div>
      <button className="px-4 py-2 rounded-lg bg-[#F47920] text-white text-[12px] font-bold whitespace-nowrap active:scale-95 transition-transform">
        Upgrade
      </button>
    </div>
  );
}

export default function CompetencyAnalysis({ coverageMatrix, locked = false }) {
  if (!coverageMatrix || Object.keys(coverageMatrix).length === 0) return null;

  const scores = Object.entries(coverageMatrix).map(([id, data], i) => {
    const scale = ALL_SCALES[id] || { name: `Scale ${id}`, shortName: `S${id}`, icon: "📊" };
    return {
      id,
      ...scale,
      score: data.score || 0,
      color: SCALE_COLORS[i % SCALE_COLORS.length]
    };
  });

  const avg = scores.reduce((sum, s) => sum + s.score, 0) / scores.length;
  const sorted = [...scores].sort((a, b) => b.score - a.score);
  const top2 = sorted.slice(0, 2);
  const bottom2 = sorted.slice(-2);

  const getGrade = (s) => {
    if (s >= 4.5) return { label: "Excellent", color: C.green };
    if (s >= 3.5) return { label: "Strong", color: C.blue };
    if (s >= 2.5) return { label: "Good", color: C.amber };
    return { label: "Developing", color: C.rose };
  };
  const grade = getGrade(avg);

  return (
    <div className="flex flex-col">
      {/* Overall Badge */}
      <div className="px-5 py-4">
        <div className={`flex items-center gap-4 p-4 rounded-2xl border ${locked ? 'bg-gray-50 border-gray-200' : 'border-opacity-30'}`} 
             style={{ 
               background: locked ? undefined : `${grade.color}10`,
               borderColor: locked ? undefined : `${grade.color}40`
             }}>
          <div className={`w-12 h-12 rounded-xl flex flex-col items-center justify-center transition-all ${locked ? 'blur-[3px] bg-gray-200' : ''}`}
               style={{ background: locked ? undefined : `${grade.color}20` }}>
            <span className="text-xl font-black leading-none" style={{ color: grade.color }}>{avg.toFixed(1)}</span>
            <span className="text-[9px] font-bold opacity-60" style={{ color: grade.color }}>/5.0</span>
          </div>
          <div className={`transition-all ${locked ? 'blur-[3px]' : ''}`}>
            <div className="text-[15px] font-bold text-slate-800">{grade.label}</div>
            <div className="text-[12px] text-slate-500">Overall performance</div>
          </div>
          {locked && <Lock className="ml-auto w-4 h-4 text-gray-400" />}
        </div>
      </div>

      {/* Radar + Bars */}
      <div className="px-5 pb-6 flex flex-col items-center gap-5">
        {scores.length >= 3 && (
          <RadarChart scores={scores} locked={locked} />
        )}

        <div className="w-full space-y-1">
          <div className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-2">
            Detailed Breakdown
          </div>
          {scores.map((s, i) => (
            <ScoreBar key={i} scaleId={s.id} label={s.name} score={s.score} color={s.color} icon={s.icon} locked={locked} />
          ))}
        </div>

        {!locked && (
          <div className="grid grid-cols-2 gap-3 w-full">
            <div className="p-3.5 rounded-xl bg-green-50 border border-green-100">
              <div className="text-[10px] font-bold text-green-600 uppercase tracking-widest mb-2">💪 Strengths</div>
              {top2.map((s, i) => (
                <div key={i} className="text-[12px] font-semibold text-slate-700 py-0.5">
                  {s.shortName} <span className="text-green-600 ml-1">{s.score.toFixed(1)}</span>
                </div>
              ))}
            </div>
            <div className="p-3.5 rounded-xl bg-purple-50 border border-purple-100">
              <div className="text-[10px] font-bold text-purple-600 uppercase tracking-widest mb-2">🌱 Growth</div>
              {bottom2.map((s, i) => (
                <div key={i} className="text-[12px] font-semibold text-slate-700 py-0.5">
                  {s.shortName} <span className="text-purple-600 ml-1">{s.score.toFixed(1)}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {locked && <UpgradeBanner />}
      </div>
    </div>
  );
}
